import axios from "axios"
import router from "@/router"
import { resolveAuthToken, clearAllAuth, clearAdminAuth } from "./auth"
import { setMenuPermissions } from "./menuPermission"
import {
  ApiBusinessError,
  getApiCode,
  getApiData,
  getApiMessage,
  getApiPayload,
  isApiSuccess,
  parseApiError
} from "./apiResult"

const request = axios.create({
  baseURL: "",
  timeout: 20000
})

let authRedirecting = false
let menuPermissionRefreshing = null
const TRACE_ID_HEADER = "X-Trace-Id"

function createTraceId() {
  const now = Date.now().toString(36)
  const random = Math.random().toString(36).slice(2, 10)
  return `fe-${now}-${random}`
}

function getTraceIdFromHeaders(headers) {
  if (!headers) return ""
  return headers[TRACE_ID_HEADER] || headers[TRACE_ID_HEADER.toLowerCase()] || ""
}

function stripQuery(url) {
  if (!url) return ""
  if (/^https?:\/\//i.test(url)) {
    try {
      const parsed = new URL(url)
      url = parsed.pathname + parsed.search
    } catch (e) {
      /* keep original url */
    }
  }
  const i = url.indexOf("?")
  return i >= 0 ? url.slice(0, i) : url
}

function normalizeMethod(method) {
  return String(method || "get").toUpperCase()
}

function safeJsonParse(value) {
  if (!value) return null
  if (typeof FormData !== "undefined" && value instanceof FormData) return null
  if (typeof value === "object") return value
  if (typeof value !== "string") return null
  try {
    return JSON.parse(value)
  } catch (e) {
    return null
  }
}

function shouldAutoLogPortalOperation(config) {
  const method = normalizeMethod(config && config.method)
  if (!["POST", "PUT", "PATCH", "DELETE"].includes(method)) return false
  const path = stripQuery((config && config.url) || "")
  if (!path.startsWith("/api/")) return false
  if (path.startsWith("/api/admin/")) return false
  if (path.startsWith("/api/portal/logs/")) return false
  if (isPublicApi(path)) return false
  if (path === "/api/crm/meta/geo/cities/resolve") return false
  return true
}

function getOperationModule(path) {
  const rules = [
    { prefix: "/api/crm/leads", code: "CRM_LEAD", name: "线索" },
    { prefix: "/api/crm/customers", code: "CRM_CUSTOMER", name: "客户" },
    { prefix: "/api/crm/opportunities", code: "CRM_OPPORTUNITY", name: "商机" },
    { prefix: "/api/crm/projects", code: "CRM_PROJECT", name: "项目" },
    { prefix: "/api/crm/contracts", code: "CRM_CONTRACT", name: "合同" },
    { prefix: "/api/crm/receipts", code: "CRM_RECEIPT", name: "回款" },
    { prefix: "/api/crm/quotes", code: "CRM_QUOTE", name: "报价单" },
    { prefix: "/api/crm/bids", code: "CRM_BID", name: "中标单" },
    { prefix: "/api/assets", code: "ASSET", name: "资产" },
    { prefix: "/api/srm/suppliers", code: "SRM_SUPPLIER", name: "供应商" },
    { prefix: "/api/workflow", code: "WORKFLOW", name: "审批中心" },
    { prefix: "/api/portal/profile", code: "PORTAL_PROFILE", name: "个人信息" }
  ]
  return rules.find(item => path.startsWith(item.prefix)) || { code: "PORTAL", name: "前台门户" }
}

function getActionType(method, path) {
  if (path.indexOf("/approve") >= 0) return "APPROVE"
  if (path.indexOf("/reject") >= 0) return "REJECT"
  if (path.indexOf("/cancel") >= 0) return "CANCEL"
  if (path.indexOf("/submit") >= 0) return "SUBMIT"
  if (path.indexOf("/publish") >= 0) return "PUBLISH"
  if (path.indexOf("/offline") >= 0) return "DISABLE"
  if (path.indexOf("/attachments") >= 0 && method === "POST") return "UPLOAD"
  if (method === "DELETE") return "DELETE"
  if (method === "PUT" || method === "PATCH") return "UPDATE"
  return "CREATE"
}

function getActionText(type) {
  const map = {
    CREATE: "新增",
    UPDATE: "修改",
    DELETE: "删除",
    SUBMIT: "提交",
    APPROVE: "审批通过",
    REJECT: "审批驳回",
    CANCEL: "取消",
    PUBLISH: "发布",
    DISABLE: "停用",
    UPLOAD: "上传附件"
  }
  return map[type] || "操作"
}

function extractBusinessId(path, payload) {
  if (payload && (payload.id || payload.businessId || payload.bizId || payload.instanceId || payload.taskId)) {
    return String(payload.id || payload.businessId || payload.bizId || payload.instanceId || payload.taskId)
  }
  const matches = path.match(/\/(\d+)(?=\/|$)/g)
  if (!matches || !matches.length) return ""
  return matches[matches.length - 1].replace("/", "")
}

function extractBusinessName(payload) {
  if (!payload || typeof payload !== "object") return ""
  const keys = [
    "leadName", "customerName", "opportunityName", "projectName", "contractName",
    "quoteName", "bidName", "assetName", "supplierName", "businessName", "name", "title"
  ]
  for (const key of keys) {
    if (payload[key]) return String(payload[key])
  }
  return ""
}

function buildPortalOperationLog(config, result, errorMessage) {
  const path = stripQuery((config && config.url) || "")
  const method = normalizeMethod(config && config.method)
  const payload = safeJsonParse(config && config.data)
  const module = getOperationModule(path)
  const actionType = getActionType(method, path)
  const actionText = getActionText(actionType)
  const businessName = extractBusinessName(payload)
  return {
    moduleCode: module.code,
    moduleName: module.name,
    actionType,
    actionName: actionText + module.name,
    businessId: extractBusinessId(path, payload),
    businessName,
    content: `${actionText}${module.name}${businessName ? "：" + businessName : ""}`,
    result,
    errorMessage: errorMessage || "",
    afterData: payload ? JSON.stringify(payload).slice(0, 10000) : ""
  }
}

function reportPortalOperation(config, result, errorMessage) {
  if (!shouldAutoLogPortalOperation(config)) return
  const token = resolveAuthToken(false)
  if (!token) return
  axios.post("/api/portal/logs/operations", buildPortalOperationLog(config, result, errorMessage), {
    headers: {
      Authorization: "Bearer " + token,
      [TRACE_ID_HEADER]: createTraceId()
    },
    timeout: 5000
  }).catch(() => {})
}

/** 无需携带 token 的接口 */
function isPublicApi(url) {
  const path = stripQuery(url)
  return path === "/api/login"
    || path.startsWith("/api/login/dingtalk/")
    || path === "/api/admin/login"
    || path === "/api/password-reset/send-code"
    || path === "/api/password-reset/confirm"
}

async function refreshMenuPermissions(jwt) {
  if (!jwt) return
  if (!menuPermissionRefreshing) {
    menuPermissionRefreshing = axios
      .get("/api/portal/my/menus", {
        headers: { Authorization: "Bearer " + jwt }
      })
      .then(res => {
        const body = getApiPayload(res)
        if (isApiSuccess(body)) {
          setMenuPermissions(getApiData(body) || [])
        }
      })
      .catch(() => {
        /* keep the existing cache when refresh fails */
      })
      .finally(() => {
        menuPermissionRefreshing = null
      })
  }
  await menuPermissionRefreshing
}

function redirectToLogin(isAdminApi) {
  const target = isAdminApi ? "/admin/login" : "/login"
  const cur = router.currentRoute && router.currentRoute.path
  if (cur === target) return
  if (authRedirecting) return
  authRedirecting = true
  if (isAdminApi) {
    clearAdminAuth()
  } else {
    clearAllAuth()
  }
  router
    .replace(target)
    .catch(() => {})
    .finally(() => {
      authRedirecting = false
    })
}

request.interceptors.request.use(
  async config => {
    const url = config.url || ""
    config.headers = config.headers || {}
    config.headers[TRACE_ID_HEADER] = config.headers[TRACE_ID_HEADER] || createTraceId()
    if (isPublicApi(url)) {
      return config
    }
    const isAdminApi = url.indexOf("/api/admin/") >= 0
    const jwt = resolveAuthToken(isAdminApi)
    if (!jwt) {
      redirectToLogin(isAdminApi)
      return Promise.reject(new Error("未登录"))
    }
    config.headers.Authorization = "Bearer " + jwt
    return config
  },
  err => Promise.reject(err)
)

request.interceptors.response.use(
  res => {
    const traceId = getTraceIdFromHeaders(res.headers)
    if (traceId) {
      res.traceId = traceId
      if (res.data && typeof res.data === "object") {
        res.data.traceId = traceId
      }
      if (process.env.NODE_ENV !== "production") {
        console.info("[traceId]", traceId, res.config && res.config.url)
      }
    }
    const payload = getApiPayload(res)
    if (payload && Object.prototype.hasOwnProperty.call(payload, "code") && !isApiSuccess(payload)) {
      reportPortalOperation(res.config, "FAIL", getApiMessage(payload, "操作失败"))
      return Promise.reject(
        new ApiBusinessError(
          getApiMessage(payload, "操作失败"),
          getApiCode(payload),
          payload,
          res
        )
      )
    }
    reportPortalOperation(res.config, "SUCCESS", "")
    return res
  },
  err => {
    const traceId = err.response && getTraceIdFromHeaders(err.response.headers)
    if (traceId) {
      err.traceId = traceId
      if (err.response) {
        err.response.traceId = traceId
        if (err.response.data && typeof err.response.data === "object") {
          err.response.data.traceId = traceId
        }
      }
      if (process.env.NODE_ENV !== "production") {
        console.info("[traceId]", traceId, err.config && err.config.url)
      }
    }
    const status = err.response && err.response.status
    if (status === 401) {
      const url = (err.config && err.config.url) || ""
      if (!isPublicApi(url)) {
        const isAdminApi = url.indexOf("/api/admin/") >= 0
        redirectToLogin(isAdminApi)
      }
    }
    if (status === 403) {
      const msg = parseApiError(err, "当前菜单没有权限，无法访问或操作")
      if (router.app && router.app.$message) {
        router.app.$message.warning(msg)
      }
    }
    reportPortalOperation(err.config, "FAIL", parseApiError(err, "操作失败"))
    return Promise.reject(err)
  }
)

export { refreshMenuPermissions }
export {
  API_SUCCESS_CODE,
  ApiResultCode,
  ApiBusinessError,
  assertApiSuccess,
  getApiData,
  getApiErrorCode,
  getApiMessage,
  getApiPayload,
  isApiSuccess,
  normalizePageResult,
  parseApiError,
  parseApiResponse
} from "./apiResult"
export default request
